/**
 * Theme runtime application (Phase 10B).
 *
 * Converte i token theme HEX del brand corrente in CSS custom properties
 * HSL ("H S% L%") sul `document.documentElement`, sovrascrivendo i default
 * Dinova viola di `globals.css :root`.
 *
 * Pattern (vs ADR-0075 BrandPreview):
 * - ADR-0075 vieta `:root` injection nel componente PREVIEW (rischio di
 *   side-effect cross-tab). Qui applichiamo via DOM API runtime con cleanup
 *   esplicito al cambio brand: NO cascade contamination, single-tab scope,
 *   safe-by-construction (HEX validato da Zod schema upstream).
 * - Tailwind v3.4 + shadcn/ui consumano via `hsl(var(--primary))`, quindi
 *   l'override CSS var si propaga automaticamente a tutto il chrome.
 */

const _APPLIED_VARS = [
  "--primary",
  "--accent",
  "--background",
  "--foreground",
  "--radius",
  "--font-family",
] as const;

/**
 * Convert "#RRGGBB" to "H S% L%" Tailwind/shadcn CSS-var format.
 * Returns null on invalid input (caller should fallback to default theme).
 */
export function hexToHslComponents(hex: string): string | null {
  const m = /^#?([a-fA-F0-9]{6})$/.exec(hex.trim());
  if (!m || !m[1]) return null;
  const raw = m[1];
  const r = parseInt(raw.slice(0, 2), 16) / 255;
  const g = parseInt(raw.slice(2, 4), 16) / 255;
  const b = parseInt(raw.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export interface BrandThemeTokens {
  primary_color?: string | null;
  secondary_color?: string | null;
  background_color?: string | null;
  text_color?: string | null;
  font_family?: string | null;
  border_radius_px?: number | null;
}

/**
 * Apply theme tokens to <html> CSS custom properties. Idempotent.
 *
 * If a hex value is malformed (Zod should catch upstream), the corresponding
 * CSS var is left untouched (default Dinova viola from globals.css `:root`).
 */
export function applyBrandTheme(theme: BrandThemeTokens): void {
  if (typeof document === "undefined") return; // SSR/node-test guard
  const root = document.documentElement;
  // The inline style set here lives on <html> and therefore OUTRANKS the
  // `.dark { ... }` rules in globals.css (inline > class specificity). The
  // brand `background_color`/`text_color` are LIGHT-mode surface colors
  // (e.g. white bg + #1F2933 text). Injecting them while dark mode is active
  // turns every foreground text dark-on-dark (contrast ~1.2 → illegible).
  // So surfaces (bg/fg) are applied only in light mode; in dark mode we clear
  // any prior injection and let the fixed `.dark` palette win. Brand identity
  // colors (primary/accent) work in both modes and are always applied.
  // BrandingThemeProvider re-runs this on every dark/light toggle.
  const isDark = root.classList.contains("dark");

  if (theme.primary_color) {
    const primaryHsl = hexToHslComponents(theme.primary_color);
    if (primaryHsl) root.style.setProperty("--primary", primaryHsl);
  }
  if (theme.secondary_color) {
    const accentHsl = hexToHslComponents(theme.secondary_color);
    if (accentHsl) root.style.setProperty("--accent", accentHsl);
  }
  if (isDark) {
    root.style.removeProperty("--background");
    root.style.removeProperty("--foreground");
  } else {
    if (theme.background_color) {
      const bgHsl = hexToHslComponents(theme.background_color);
      if (bgHsl) root.style.setProperty("--background", bgHsl);
    }
    if (theme.text_color) {
      const fgHsl = hexToHslComponents(theme.text_color);
      if (fgHsl) root.style.setProperty("--foreground", fgHsl);
    }
  }
  if (theme.font_family) {
    root.style.setProperty("--font-family", theme.font_family);
  }
  if (theme.border_radius_px != null) {
    root.style.setProperty("--radius", `${theme.border_radius_px}px`);
  }
}

/** Remove all overrides — fallback a globals.css `:root` defaults. */
export function resetBrandTheme(): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  for (const v of _APPLIED_VARS) {
    root.style.removeProperty(v);
  }
}

/**
 * Phase 11.8: dynamically update the document `<link rel="icon">` to the
 * brand favicon URL.
 *
 * The static `index.html` ships `<link rel="icon" href="/favicon.svg">` — that
 * is the fallback for the DPanel default chrome and per-request favicon
 * during the bundle download window. When `/branding/current` resolves to a
 * brand with a `favicon_url` set, this helper rewrites the `href` on every
 * existing icon link (browsers may show stale until the URL diff is
 * observed). Passing `null` restores the default.
 *
 * Behaviour:
 * - Updates ALL `<link rel="icon">` already in head (some Vite plugins inject
 *   multiple sizes; we touch them all).
 * - If none exist (defensive), creates one with type unspecified — browser
 *   sniffs from the URL extension (PNG/SVG/WEBP are all OK; brand assets are
 *   PNG/JPEG/WEBP per CLAUDE.md §8, SVG vietato).
 * - Idempotent: same URL → no-op (avoids browser fetch spam on every render).
 * - Cache-busting NOT injected: the BE asset URL is content-hashed via the
 *   brand_assets row (sha256 changes with file) and nginx returns a long
 *   max-age + ETag. If the brand uploads a NEW favicon (same kind), the
 *   `public_url` changes, the React Query refetch propagates, and the
 *   `href` diff triggers a fresh fetch.
 */
const _DEFAULT_FAVICON_URL = "/favicon.svg";

export function applyBrandFavicon(url: string | null | undefined): void {
  if (typeof document === "undefined") return;
  const target = url ?? _DEFAULT_FAVICON_URL;
  const links = document.head.querySelectorAll<HTMLLinkElement>('link[rel="icon"]');
  if (links.length === 0) {
    const link = document.createElement("link");
    link.rel = "icon";
    link.href = target;
    document.head.appendChild(link);
    return;
  }
  for (const link of links) {
    if (link.getAttribute("href") === target) continue; // idempotent
    link.removeAttribute("type"); // let browser sniff from URL; brand favicons are PNG/WEBP, not SVG
    link.setAttribute("href", target);
  }
}
